import { useModal } from "@/Contexts";
import { ThreeDotsIcon } from "@/Icons/v2/ThreeDots";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { ConfirmModal } from "@/components/ConfirmModal";
import { LibraryPageContext } from "@/pages/library_v2/components/filesView/context";
import type { LibraryFolder } from "@/pages/library_v2/components/filesView/types";
import { useTrans } from "@/translations";

import type React from "react";
import { useCallback, useContext } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

type Props = {
  folder: LibraryFolder;
  setIsRenaming: (value: boolean) => void;
};

export const FolderActionsView: React.FC<Props> = ({
  folder,
  setIsRenaming,
}) => {
  const { t } = useTrans();
  const { showModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();
  const { refetchFolders, setSelectedFolderName } =
    useContext(LibraryPageContext);

  const { mutate: deleteFolder } = trpc.library.deleteFolder.useMutation({
    onSuccess: () => {
      refetchFolders();
      setSelectedFolderName("");
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentDeleteFailed",
      });
      console.error("Error deleting document:", error);
    },
  });

  const onDeleteFolder = useCallback(async () => {
    await deleteFolder({
      terminalId: currentTerminal?.id!,
      folderName: folder.name,
    });
  }, [currentTerminal?.id, deleteFolder, folder.name]);

  const openDeleteFolderModal = useCallback(() => {
    showModal(
      <ConfirmModal
        title={t("libraryPage:folderListConfirmDeleteFolder", {
          folderName: folder?.name,
        })}
        hint={t("libraryPage:folderListConfirmDeleteFolderDescription")}
        onConfirm={onDeleteFolder}
      />,
    );
  }, [showModal, t, onDeleteFolder, folder]);

  return (
    <Popover>
      <PopoverButton className="text-grey6 hover:bg-grey2 mr-1 flex h-[26px] w-6 flex-none cursor-pointer items-center justify-center rounded-[5px] opacity-0 group-hover:opacity-100 hover:text-[#1D1C1C] focus:opacity-100">
        <ThreeDotsIcon />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: "bottom end", gap: 4 }}
        className="border-grey3 z-[10000] w-[176px] rounded-lg border bg-white p-[5px] shadow-lg"
      >
        {({ close }) => (
          <>
            <button
              type="button"
              onClick={() => {
                close();
                setIsRenaming(true);
              }}
              className="hover:bg-grey1 block w-full cursor-pointer rounded-[5px] px-[10px] py-2 text-left text-[12.5px] text-[#1D1C1C]"
            >
              {t("libraryPage:folderListRenameFolder")}
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                openDeleteFolderModal();
              }}
              className="block w-full cursor-pointer rounded-[5px] px-[10px] py-2 text-left text-[12.5px] text-[#B73A23] hover:bg-[#FBEDE9]"
            >
              {t("libraryPage:folderListDeleteFolder")}
            </button>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
